import React, { useState, useEffect } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  ScrollView,
  RefreshControl,
  StatusBar,
  Alert,
  Modal,
  FlatList,
  ActivityIndicator,
  TextInput,
} from 'react-native';
import {
  MaterialCommunityIcons,
} from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

// ===== API Configuration =====
const API_BASE_URL = 'https://api.jobeng.ai';

const getCompanyId = async () => {
  try {
    const userData = await AsyncStorage.getItem('currentUser');
    if (userData) {
      const user = JSON.parse(userData);
      return user.company_id || user.companyId || null;
    }
    const companyProfile = await AsyncStorage.getItem('companyProfile');
    if (companyProfile) {
      const company = JSON.parse(companyProfile);
      return company.id || company.company_id || null;
    }
    return null;
  } catch (e) {
    console.error('Error getting company_id:', e);
    return null;
  }
};

const apiRequest = async (endpoint, options = {}) => {
  try {
    const companyId = await getCompanyId();
    // Split endpoint and query string
    const [path, queryString] = endpoint.split('?');
    // Route: auth/* goes to /api/auth/*, everything else to /api/data/{table}
    let baseUrl;
    if (path.startsWith('auth/')) {
      baseUrl = `${API_BASE_URL}/api/${path}`;
    } else {
      // Convert hyphens to underscores for DB table mapping
      const dbPath = path.replace(/-/g, '_');
      baseUrl = `${API_BASE_URL}/api/data/${dbPath}`;
    }
    const params = new URLSearchParams(queryString || '');
    if (companyId) params.set('company_id', companyId);
    const separator = params.toString() ? '?' : '';
    const url = `${baseUrl}${separator}${params.toString()}`;

    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
      ...options,
      ...(options.body ? { body: JSON.stringify(options.body) } : {}),
    });

    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`API request failed for ${endpoint}:`, error);
    throw error;
  }
};
// ===== End API Configuration =====


const { width, height } = Dimensions.get('window');

export default function ProposalBuilderScreen({ navigation }) {
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('proposals');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showProposalModal, setShowProposalModal] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Proposal form data
  const [proposalForm, setProposalForm] = useState({
    title: '',
    clientName: '',
    clientEmail: '',
    clientCompany: '',
    projectDescription: '',
    objectives: '',
    timeline: '',
    budget: '',
    deliverables: [],
    terms: '',
    validUntil: '',
    sections: []
  });

  // Existing proposals
  const [proposals, setProposals] = useState([
    {
      id: 'PROP-001',
      title: 'Enterprise CRM Implementation',
      clientName: 'John Smith',
      clientEmail: 'john.smith@techcorp.com',
      clientCompany: 'TechCorp Ltd',
      projectDescription: 'Complete CRM system implementation with custom integrations and training',
      status: 'sent',
      createdDate: '2024-01-15T10:30:00Z',
      sentDate: '2024-01-15T14:20:00Z',
      validUntil: '2024-02-15T23:59:59Z',
      budget: 285000.00,
      timeline: '6 months',
      objectives: [
        'Streamline sales processes',
        'Improve customer data management',
        'Increase sales team productivity',
        'Enhance reporting capabilities'
      ],
      deliverables: [
        { id: '1', title: 'CRM System Setup', description: 'Complete system configuration and customization', timeline: 'Month 1' },
        { id: '2', title: 'Data Migration', description: 'Transfer existing customer data to new system', timeline: 'Month 2' },
        { id: '3', title: 'Integration Development', description: 'Custom integrations with existing tools', timeline: 'Month 3-4' },
        { id: '4', title: 'User Training', description: 'Comprehensive training for all users', timeline: 'Month 5' },
        { id: '5', title: 'Go-Live Support', description: 'Post-implementation support and optimization', timeline: 'Month 6' }
      ],
      sections: [
        {
          id: '1',
          title: 'Executive Summary',
          content: 'This proposal outlines a comprehensive CRM implementation strategy designed to transform TechCorp\'s sales operations and customer management processes.',
          order: 1
        },
        {
          id: '2',
          title: 'Project Scope',
          content: 'The project includes system setup, data migration, custom integrations, user training, and ongoing support to ensure successful adoption.',
          order: 2
        },
        {
          id: '3',
          title: 'Implementation Methodology',
          content: 'We will follow an agile implementation approach with regular checkpoints and stakeholder feedback sessions.',
          order: 3
        }
      ],
      terms: 'Payment in 3 installments: 40% upfront, 40% at go-live, 20% after 30 days',
      notes: 'Includes 1-year support and maintenance'
    },
    {
      id: 'PROP-002',
      title: 'Digital Marketing Strategy',
      clientName: 'Sarah Johnson',
      clientEmail: 'sarah.j@greenbuilders.com',
      clientCompany: 'Green Builders Ltd',
      projectDescription: 'Comprehensive digital marketing strategy and implementation for sustainable construction company',
      status: 'draft',
      createdDate: '2024-01-18T09:15:00Z',
      sentDate: null,
      validUntil: '2024-02-18T23:59:59Z',
      budget: 125000.00,
      timeline: '12 months',
      objectives: [
        'Increase brand awareness in sustainable construction',
        'Generate qualified leads through digital channels',
        'Establish thought leadership in green building',
        'Improve online presence and SEO rankings'
      ],
      deliverables: [
        { id: '1', title: 'Marketing Strategy Document', description: 'Comprehensive digital marketing strategy', timeline: 'Month 1' },
        { id: '2', title: 'Website Redesign', description: 'Modern, SEO-optimized website', timeline: 'Month 2-3' },
        { id: '3', title: 'Content Marketing Program', description: 'Blog, case studies, and thought leadership content', timeline: 'Ongoing' },
        { id: '4', title: 'Social Media Management', description: 'LinkedIn, Twitter, and Instagram management', timeline: 'Ongoing' },
        { id: '5', title: 'Lead Generation Campaigns', description: 'Targeted PPC and social media campaigns', timeline: 'Month 4-12' }
      ],
      sections: [
        {
          id: '1',
          title: 'Current State Analysis',
          content: 'Analysis of current digital presence and competitive landscape in the sustainable construction market.',
          order: 1
        },
        {
          id: '2',
          title: 'Strategic Recommendations',
          content: 'Detailed recommendations for improving digital marketing effectiveness and lead generation.',
          order: 2
        }
      ],
      terms: 'Monthly retainer with quarterly performance reviews',
      notes: 'Includes monthly reporting and strategy adjustments'
    },
    {
      id: 'PROP-003',
      title: 'Office Space Renovation',
      clientName: 'Mike Chen',
      clientEmail: 'mike.chen@urbandev.com',
      clientCompany: 'Urban Development Co',
      projectDescription: 'Complete office renovation with modern design and sustainable materials',
      status: 'accepted',
      createdDate: '2024-01-10T11:45:00Z',
      sentDate: '2024-01-10T16:30:00Z',
      acceptedDate: '2024-01-12T14:20:00Z',
      validUntil: '2024-02-10T23:59:59Z',
      budget: 385000.00,
      timeline: '4 months',
      objectives: [
        'Create modern, collaborative workspace',
        'Improve employee satisfaction and productivity',
        'Implement sustainable design principles',
        'Optimize space utilization'
      ],
      deliverables: [
        { id: '1', title: 'Design Consultation', description: 'Space planning and design concepts', timeline: 'Month 1' },
        { id: '2', title: 'Construction Phase 1', description: 'Demolition and structural work', timeline: 'Month 2' },
        { id: '3', title: 'Construction Phase 2', description: 'Installation and finishing work', timeline: 'Month 3' },
        { id: '4', title: 'Final Inspection', description: 'Quality assurance and handover', timeline: 'Month 4' }
      ],
      sections: [
        {
          id: '1',
          title: 'Design Philosophy',
          content: 'Our approach focuses on creating a modern, sustainable workspace that enhances collaboration and productivity.',
          order: 1
        },
        {
          id: '2',
          title: 'Project Timeline',
          content: 'Detailed timeline with milestones and key deliverables for each phase of the renovation.',
          order: 2
        }
      ],
      terms: 'Payment schedule: 25% deposit, 50% at midpoint, 25% on completion',
      notes: 'Includes 2-year warranty on all work'
    },
    {
      id: 'PROP-004',
      title: 'Training Program Development',
      clientName: 'Emma Wilson',
      clientEmail: 'emma.w@buildfast.com',
      clientCompany: 'BuildFast Construction',
      projectDescription: 'Custom safety training program for construction workers',
      status: 'rejected',
      createdDate: '2024-01-05T08:20:00Z',
      sentDate: '2024-01-05T12:15:00Z',
      rejectedDate: '2024-01-08T10:30:00Z',
      validUntil: '2024-01-20T23:59:59Z',
      budget: 75000.00,
      timeline: '3 months',
      objectives: [
        'Improve workplace safety compliance',
        'Reduce accident rates and insurance costs',
        'Enhance worker safety awareness',
        'Meet regulatory requirements'
      ],
      deliverables: [
        { id: '1', title: 'Training Curriculum', description: 'Comprehensive safety training curriculum', timeline: 'Month 1' },
        { id: '2', title: 'Training Materials', description: 'Manuals, videos, and interactive content', timeline: 'Month 2' },
        { id: '3', title: 'Instructor Training', description: 'Train internal trainers', timeline: 'Month 2' },
        { id: '4', title: 'Program Launch', description: 'Roll out training to all workers', timeline: 'Month 3' }
      ],
      sections: [
        {
          id: '1',
          title: 'Training Needs Assessment',
          content: 'Analysis of current safety practices and identification of training gaps.',
          order: 1
        }
      ],
      terms: 'Fixed price with milestone payments',
      notes: 'Includes OSHA compliance certification'
    },
    {
      id: 'PROP-005',
      title: 'IT Infrastructure Upgrade',
      clientName: 'David Brown',
      clientEmail: 'david.brown@constructplus.com',
      clientCompany: 'Construct Plus',
      projectDescription: 'Complete IT infrastructure modernization and cloud migration',
      status: 'under_review',
      createdDate: '2024-01-08T13:30:00Z',
      sentDate: '2024-01-08T15:45:00Z',
      validUntil: '2024-02-08T23:59:59Z',
      budget: 195000.00,
      timeline: '5 months',
      objectives: [
        'Modernize legacy IT systems',
        'Improve system reliability and performance',
        'Enable remote work capabilities',
        'Enhance data security and backup'
      ],
      deliverables: [
        { id: '1', title: 'Infrastructure Assessment', description: 'Current state analysis and recommendations', timeline: 'Month 1' },
        { id: '2', title: 'Cloud Migration', description: 'Migrate systems to cloud infrastructure', timeline: 'Month 2-3' },
        { id: '3', title: 'Security Implementation', description: 'Enhanced security measures and protocols', timeline: 'Month 4' },
        { id: '4', title: 'Training and Support', description: 'User training and ongoing support setup', timeline: 'Month 5' }
      ],
      sections: [
        {
          id: '1',
          title: 'Current Challenges',
          content: 'Overview of existing IT infrastructure limitations and business impact.',
          order: 1
        },
        {
          id: '2',
          title: 'Proposed Solution',
          content: 'Detailed technical solution with cloud migration strategy and security enhancements.',
          order: 2
        }
      ],
      terms: 'Phased payments aligned with project milestones',
      notes: 'Includes 6-month post-implementation support'
    }
  ]);

  // Proposal templates
  const [proposalTemplates, setProposalTemplates] = useState([
    {
      id: '1',
      name: 'Software Implementation',
      description: 'Template for software projects and system implementations',
      category: 'Technology',
      sections: [
        { title: 'Executive Summary', content: 'Project overview and key benefits' },
        { title: 'Current State Analysis', content: 'Assessment of existing systems and processes' },
        { title: 'Proposed Solution', content: 'Detailed technical solution and approach' },
        { title: 'Implementation Plan', content: 'Project timeline and methodology' },
        { title: 'Investment and ROI', content: 'Cost breakdown and return on investment' }
      ],
      defaultTimeline: '6 months',
      defaultTerms: 'Payment in 3 installments based on milestones'
    },
    {
      id: '2',
      name: 'Marketing Strategy',
      description: 'Template for marketing and branding projects',
      category: 'Marketing',
      sections: [
        { title: 'Market Analysis', content: 'Current market position and competitive landscape' },
        { title: 'Strategic Objectives', content: 'Marketing goals and success metrics' },
        { title: 'Recommended Approach', content: 'Marketing strategy and tactics' },
        { title: 'Campaign Timeline', content: 'Implementation schedule and milestones' },
        { title: 'Budget and Resources', content: 'Investment requirements and resource allocation' }
      ],
      defaultTimeline: '12 months',
      defaultTerms: 'Monthly retainer with quarterly reviews'
    },
    {
      id: '3',
      name: 'Construction Project',
      description: 'Template for construction and renovation projects',
      category: 'Construction',
      sections: [
        { title: 'Project Overview', content: 'Scope of work and project objectives' },
        { title: 'Design Approach', content: 'Design philosophy and methodology' },
        { title: 'Construction Plan', content: 'Detailed construction timeline and phases' },
        { title: 'Quality Assurance', content: 'Quality control measures and standards' },
        { title: 'Project Investment', content: 'Cost breakdown and payment schedule' }
      ],
      defaultTimeline: '4 months',
      defaultTerms: 'Progress payments based on completion milestones'
    },
    {
      id: '4',
      name: 'Training Program',
      description: 'Template for training and development projects',
      category: 'Education',
      sections: [
        { title: 'Training Needs Assessment', content: 'Analysis of current skills and training gaps' },
        { title: 'Learning Objectives', content: 'Specific learning outcomes and goals' },
        { title: 'Training Methodology', content: 'Approach and delivery methods' },
        { title: 'Implementation Schedule', content: 'Training rollout timeline' },
        { title: 'Investment and Value', content: 'Cost and expected business impact' }
      ],
      defaultTimeline: '3 months',
      defaultTerms: 'Fixed price with milestone payments'
    }
  ]);

  // Proposal statistics
  const [proposalStats, setProposalStats] = useState({
    totalProposals: 5,
    draftProposals: 1,
    sentProposals: 1,
    acceptedProposals: 1,
    rejectedProposals: 1,
    underReviewProposals: 1,
    totalValue: 1065000.00,
    acceptedValue: 385000.00,
    winRate: 20.0,
    avgProposalValue: 213000.00
  });

  useEffect(() => {
    initializeScreen();
  }, []);

  const initializeScreen = async () => {
    try {
      setIsLoading(true);
      // Load proposals from API
      try {
        const [proposalsResp, templatesResp, statsResp] = await Promise.all([
          apiRequest('proposals'),
          apiRequest('proposal-templates'),
          apiRequest('proposal-stats'),
        ]);
        if (proposalsResp.success && proposalsResp.data) setProposals(proposalsResp.data);
        if (templatesResp.success && templatesResp.data) setProposalTemplates(templatesResp.data);
        if (statsResp.success && statsResp.data) setProposalStats(statsResp.data);
      } catch (apiError) {
        console.warn('API unavailable, using local data:', apiError.message);
      }
    } catch (error) {
      console.error('Error initializing screen:', error);
      Alert.alert('Error', 'Failed to load proposal builder data');
    } finally {
      setIsLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setRefreshing(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'draft': '#6B7280',
      'sent': '#3B82F6',
      'accepted': '#10B981',
      'rejected': '#EF4444',
      'under_review': '#F59E0B'
    };
    return colors[status] || '#6B7280';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'draft': 'file-edit',
      'sent': 'send',
      'accepted': 'check-circle',
      'rejected': 'close-circle',
      'under_review': 'clock'
    };
    return icons[status] || 'file';
  };

  const filteredProposals = proposals.filter(proposal =>
    String(proposal.title ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    String(proposal.clientName ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    String(proposal.clientCompany ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    String(proposal.id ?? '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreateProposal = () => {
    setProposalForm({
      title: '',
      clientName: '',
      clientEmail: '',
      clientCompany: '',
      projectDescription: '',
      objectives: '',
      timeline: '',
      budget: '',
      deliverables: [],
      terms: '',
      validUntil: '',
      sections: []
    });
    setShowCreateModal(true);
  };

  const handleSaveProposal = () => {
    if (!proposalForm.title || !proposalForm.clientName || !proposalForm.projectDescription) {
      Alert.alert('Error', 'Please fill in all required fields');
      return;
    }

    const newProposal = {
      id: `PROP-${String(proposals.length + 1).padStart(3, '0')}`,
      ...proposalForm,
      status: 'draft',
      createdDate: new Date().toISOString(),
      sentDate: null,
      budget: parseFloat(proposalForm.budget) || 0,
      objectives: proposalForm.objectives.split('\n').filter(obj => obj.trim()),
      deliverables: proposalForm.deliverables,
      sections: proposalForm.sections
    };

    setProposals([newProposal, ...proposals]);
    setShowCreateModal(false);
    Alert.alert('Success', 'Proposal created successfully');
  };

  const handleSendProposal = (proposalId) => {
    setProposals(proposals.map(proposal => 
      proposal.id === proposalId 
        ? { ...proposal, status: 'sent', sentDate: new Date().toISOString() }
        : proposal
    ));
    Alert.alert('Success', 'Proposal sent successfully');
  };

  const handleDuplicateProposal = (proposal) => {
    const duplicatedProposal = {
      ...proposal,
      id: `PROP-${String(proposals.length + 1).padStart(3, '0')}`,
      title: `${proposal.title} (Copy)`,
      status: 'draft',
      createdDate: new Date().toISOString(),
      sentDate: null,
      acceptedDate: null,
      rejectedDate: null
    };
    setProposals([duplicatedProposal, ...proposals]);
    Alert.alert('Success', 'Proposal duplicated successfully');
  };

  const addDeliverable = () => {
    const newDeliverable = {
      id: String(proposalForm.deliverables.length + 1),
      title: '',
      description: '',
      timeline: ''
    };
    setProposalForm({
      ...proposalForm,
      deliverables: [...proposalForm.deliverables, newDeliverable]
    });
  };

  const updateDeliverable = (deliverableId, field, value) => {
    const updatedDeliverables = proposalForm.deliverables.map(deliverable => 
      deliverable.id === deliverableId 
        ? { ...deliverable, [field]: value }
        : deliverable
    );
    setProposalForm({ ...proposalForm, deliverables: updatedDeliverables });
  };

  const removeDeliverable = (deliverableId) => {
    setProposalForm({
      ...proposalForm,
      deliverables: proposalForm.deliverables.filter(deliverable => deliverable.id !== deliverableId)
    });
  };

  const addSection = () => {
    const newSection = {
      id: String(proposalForm.sections.length + 1),
      title: '',
      content: '',
      order: proposalForm.sections.length + 1
    };
    setProposalForm({
      ...proposalForm,
      sections: [...proposalForm.sections, newSection]
    });
  };

  const updateSection = (sectionId, field, value) => {
    const updatedSections = proposalForm.sections.map(section => 
      section.id === sectionId 
        ? { ...section, [field]: value }
        : section
    );
    setProposalForm({ ...proposalForm, sections: updatedSections });
  };

  const removeSection = (sectionId) => {
    setProposalForm({
      ...proposalForm,
      sections: proposalForm.sections.filter(section => section.id !== sectionId)
    });
  };

  const renderHeader = () => (
    <LinearGradient
      colors={['#1F2937', '#374151']}
      style={styles.header}
    >
      <View style={styles.headerContent}>
        <TouchableOpacity 
          onPress={() => navigation?.goBack()}
          style={styles.backButton}
        >
          <MaterialCommunityIcons name="arrow-left" size={24} color="#FFFFFF" />
        </TouchableOpacity>
        
        <View style={styles.headerCenter}>
          <Text style={styles.headerTitle}>Proposal Builder</Text>
          <Text style={styles.headerSubtitle}>Create Professional Proposals</Text>
        </View>
        
        <TouchableOpacity 
          style={styles.headerButton}
          onPress={handleCreateProposal}
        >
          <MaterialCommunityIcons name="plus" size={24} color="#FFFFFF" />
        </TouchableOpacity>
      </View>
    </LinearGradient>
  );

  const renderTabs = () => (
    <View style={styles.tabsContainer}>
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.tabsContent}
      >
        {[
          { key: 'proposals', title: 'Proposals', icon: 'file-document' },
          { key: 'templates', title: 'Templates', icon: 'file-multiple' },
          { key: 'analytics', title: 'Analytics', icon: 'chart-line' }
        ].map((tab) => (
          <TouchableOpacity
            key={tab.key}
            style={[
              styles.tab,
              activeTab === tab.key && styles.activeTab
            ]}
            onPress={() => setActiveTab(tab.key)}
          >
            <MaterialCommunityIcons 
              name={tab.icon} 
              size={20} 
              color={activeTab === tab.key ? '#3B82F6' : '#6B7280'} 
            />
            <Text style={[
              styles.tabText,
              activeTab === tab.key && styles.activeTabText
            ]}>
              {tab.title}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderSearchBar = () => (
    <View style={styles.searchContainer}>
      <View style={styles.searchBar}>
        <MaterialCommunityIcons name="magnify" size={20} color="#6B7280" />
        <TextInput
          style={styles.searchInput}
          placeholder="Search proposals..."
          value={searchQuery}
          onChangeText={setSearchQuery}
          placeholderTextColor="#9CA3AF"
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <MaterialCommunityIcons name="close" size={20} color="#6B7280" />
          </TouchableOpacity>
        )}
      </View>
    </View>
  );

  const renderProposalStats = () => {
    const stats = [
      {
        title: 'Total Proposals',
        value: proposalStats.totalProposals.toString(),
        icon: 'file-document-multiple',
        color: '#3B82F6'
      },
      {
        title: 'Accepted',
        value: proposalStats.acceptedProposals.toString(),
        icon: 'check-circle',
        color: '#10B981'
      },
      {
        title: 'Total Value',
        value: formatCurrency(proposalStats.totalValue),
        icon: 'currency-usd',
        color: '#F59E0B'
      },
      {
        title: 'Win Rate',
        value: `${proposalStats.winRate}%`,
        icon: 'percent',
        color: '#8B5CF6'
      }
    ];

  
  // Save proposal via API
  const saveProposalToApi = async (proposal) => {
    try {
      await apiRequest('proposals', {
        method: proposal.id ? 'PUT' : 'POST',
        body: proposal,
      });
    } catch (error) {
      console.warn('API save failed:', error.message);
    }
  };

  return (
      <FlatList
        data={stats}
        keyExtractor={(item, index) => index.toString()}
        renderItem={({ item }) => (
          <View style={styles.statCard}>
            <View style={[styles.statIcon, { backgroundColor: item.color }]}>
              <MaterialCommunityIcons name={item.icon} size={24} color="#FFFFFF" />
            </View>
            <Text style={styles.statValue}>{item.value}</Text>
            <Text style={styles.statTitle}>{item.title}</Text>
          </View>
        )}
        numColumns={2}
        columnWrapperStyle={styles.row}
        contentContainerStyle={styles.statsContainer}
        scrollEnabled={false}
      />
    );
  };

  const renderProposalsList = () => (
    <FlatList
      data={filteredProposals}
      keyExtractor={(item) => item.id}
      renderItem={({ item }) => (
        <TouchableOpacity 
          style={styles.proposalCard}
          onPress={() => {
            setSelectedProposal(item);
            setShowProposalModal(true);
          }}
        >
          <View style={styles.proposalHeader}>
            <View style={styles.proposalInfo}>
              <Text style={styles.proposalId}>{item.id}</Text>
              <Text style={styles.proposalTitle}>{item.title}</Text>
              <Text style={styles.proposalClient}>{item.clientName} - {item.clientCompany}</Text>
            </View>
            <View style={styles.proposalStatus}>
              <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) }]}>
                <MaterialCommunityIcons 
                  name={getStatusIcon(item.status)} 
                  size={12} 
                  color="#FFFFFF" 
                />
                <Text style={styles.statusText}>{item.status.replace('_', ' ')}</Text>
              </View>
            </View>
          </View>
          
          <Text style={styles.proposalDescription} numberOfLines={2}>
            {item.projectDescription}
          </Text>
          
          <View style={styles.proposalDetails}>
            <View style={styles.proposalDetail}>
              <Text style={styles.proposalDetailLabel}>Budget</Text>
              <Text style={styles.proposalDetailValue}>{formatCurrency(item.budget)}</Text>
            </View>
            <View style={styles.proposalDetail}>
              <Text style={styles.proposalDetailLabel}>Timeline</Text>
              <Text style={styles.proposalDetailValue}>{item.timeline}</Text>
            </View>
            <View style={styles.proposalDetail}>
              <Text style={styles.proposalDetailLabel}>Created</Text>
              <Text style={styles.proposalDetailValue}>{formatDate(item.createdDate)}</Text>
            </View>
          </View>
          
          <View style={styles.proposalActions}>
            {item.status === 'draft' && (
              <TouchableOpacity 
                style={[styles.actionButton, { backgroundColor: '#3B82F6' }]}
                onPress={() => handleSendProposal(item.id)}
              >
                <MaterialCommunityIcons name="send" size={16} color="#FFFFFF" />
                <Text style={styles.actionButtonText}>Send</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity 
              style={[styles.actionButton, { backgroundColor: '#6B7280' }]}
              onPress={() => handleDuplicateProposal(item)}
            >
              <MaterialCommunityIcons name="content-copy" size={16} color="#FFFFFF" />
              <Text style={styles.actionButtonText}>Duplicate</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      )}
      contentContainerStyle={styles.listContainer}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          colors={['#3B82F6']}
        />
      }
      showsVerticalScrollIndicator={false}
    />
  );

  const renderTemplates = () => (
    <FlatList
      data={proposalTemplates}
      keyExtractor={(item) => item.id}
      renderItem={({ item }) => (
        <TouchableOpacity 
          style={styles.templateCard}
          onPress={() => {
            setProposalForm({
              ...proposalForm,
              sections: item.sections.map((section, index) => ({
                id: String(index + 1),
                title: section.title,
                content: section.content,
                order: index + 1
              })),
              timeline: item.defaultTimeline,
              terms: item.defaultTerms
            });
            setShowCreateModal(true);
          }}
        >
          <View style={styles.templateHeader}>
            <Text style={styles.templateName}>{item.name}</Text>
            <View style={styles.templateCategory}>
              <Text style={styles.templateCategoryText}>{item.category}</Text>
            </View>
          </View>
          <Text style={styles.templateDescription}>{item.description}</Text>
          <View style={styles.templateDetails}>
            <Text style={styles.templateDetail}>Sections: {item.sections.length}</Text>
            <Text style={styles.templateDetail}>Timeline: {item.defaultTimeline}</Text>
          </View>
        </TouchableOpacity>
      )}
      numColumns={2}
      columnWrapperStyle={styles.row}
      contentContainerStyle={styles.listContainer}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          colors={['#3B82F6']}
        />
      }
      showsVerticalScrollIndicator={false}
    />
  );

  const renderAnalytics = () => (
    <ScrollView 
      style={styles.contentContainer}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          colors={['#3B82F6']}
        />
      }
      showsVerticalScrollIndicator={false}
    >
      {renderProposalStats()}
      
      <View style={styles.analyticsSection}>
        <Text style={styles.sectionTitle}>Proposal Status Distribution</Text>
        <View style={styles.statusDistribution}>
          {[
            { status: 'draft', count: proposalStats.draftProposals, color: '#6B7280' },
            { status: 'sent', count: proposalStats.sentProposals, color: '#3B82F6' },
            { status: 'accepted', count: proposalStats.acceptedProposals, color: '#10B981' },
            { status: 'rejected', count: proposalStats.rejectedProposals, color: '#EF4444' },
            { status: 'under review', count: proposalStats.underReviewProposals, color: '#F59E0B' }
          ].map((item, index) => (
            <View key={index} style={styles.statusItem}>
              <View style={[styles.statusIndicator, { backgroundColor: item.color }]} />
              <Text style={styles.statusLabel}>{item.status}</Text>
              <Text style={styles.statusCount}>{item.count}</Text>
            </View>
          ))}
        </View>
      </View>
    </ScrollView>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'proposals':
        return (
          <View style={styles.contentContainer}>
            {renderSearchBar()}
            {renderProposalsList()}
          </View>
        );
      case 'templates':
        return renderTemplates();
      case 'analytics':
        return renderAnalytics();
      default:
        return null;
    }
  };

  const renderCreateModal = () => (
    <Modal
      visible={showCreateModal}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => setShowCreateModal(false)}
    >
      <View style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>Create New Proposal</Text>
          <TouchableOpacity 
            style={styles.modalCloseButton}
            onPress={() => setShowCreateModal(false)}
          >
            <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
          </TouchableOpacity>
        </View>
        
        <ScrollView style={styles.modalContent}>
          <View style={styles.formSection}>
            <Text style={styles.formSectionTitle}>Basic Information</Text>
            <TextInput
              style={styles.formInput}
              placeholder="Proposal Title *"
              value={proposalForm.title}
              onChangeText={(text) => setProposalForm({ ...proposalForm, title: text })}
            />
            <TextInput
              style={styles.formInput}
              placeholder="Client Name *"
              value={proposalForm.clientName}
              onChangeText={(text) => setProposalForm({ ...proposalForm, clientName: text })}
            />
            <TextInput
              style={styles.formInput}
              placeholder="Client Email"
              value={proposalForm.clientEmail}
              onChangeText={(text) => setProposalForm({ ...proposalForm, clientEmail: text })}
              keyboardType="email-address"
            />
            <TextInput
              style={styles.formInput}
              placeholder="Client Company"
              value={proposalForm.clientCompany}
              onChangeText={(text) => setProposalForm({ ...proposalForm, clientCompany: text })}
            />
          </View>
          
          <View style={styles.formSection}>
            <Text style={styles.formSectionTitle}>Project Details</Text>
            <TextInput
              style={[styles.formInput, styles.textArea]}
              placeholder="Project Description *"
              value={proposalForm.projectDescription}
              onChangeText={(text) => setProposalForm({ ...proposalForm, projectDescription: text })}
              multiline
              numberOfLines={3}
            />
            <TextInput
              style={[styles.formInput, styles.textArea]}
              placeholder="Project Objectives (one per line)"
              value={proposalForm.objectives}
              onChangeText={(text) => setProposalForm({ ...proposalForm, objectives: text })}
              multiline
              numberOfLines={4}
            />
            <TextInput
              style={styles.formInput}
              placeholder="Timeline"
              value={proposalForm.timeline}
              onChangeText={(text) => setProposalForm({ ...proposalForm, timeline: text })}
            />
            <TextInput
              style={styles.formInput}
              placeholder="Budget"
              value={proposalForm.budget}
              onChangeText={(text) => setProposalForm({ ...proposalForm, budget: text })}
              keyboardType="numeric"
            />
          </View>
          
          <View style={styles.formSection}>
            <View style={styles.formSectionHeader}>
              <Text style={styles.formSectionTitle}>Deliverables</Text>
              <TouchableOpacity 
                style={styles.addButton}
                onPress={addDeliverable}
              >
                <MaterialCommunityIcons name="plus" size={20} color="#3B82F6" />
                <Text style={styles.addButtonText}>Add Deliverable</Text>
              </TouchableOpacity>
            </View>
            
            {proposalForm.deliverables.map((deliverable, index) => (
              <View key={deliverable.id} style={styles.deliverableForm}>
                <View style={styles.deliverableHeader}>
                  <Text style={styles.deliverableTitle}>Deliverable {index + 1}</Text>
                  <TouchableOpacity 
                    onPress={() => removeDeliverable(deliverable.id)}
                    style={styles.removeButton}
                  >
                    <MaterialCommunityIcons name="close" size={16} color="#EF4444" />
                  </TouchableOpacity>
                </View>
                <TextInput
                  style={styles.formInput}
                  placeholder="Title"
                  value={deliverable.title}
                  onChangeText={(text) => updateDeliverable(deliverable.id, 'title', text)}
                />
                <TextInput
                  style={[styles.formInput, styles.textArea]}
                  placeholder="Description"
                  value={deliverable.description}
                  onChangeText={(text) => updateDeliverable(deliverable.id, 'description', text)}
                  multiline
                  numberOfLines={2}
                />
                <TextInput
                  style={styles.formInput}
                  placeholder="Timeline"
                  value={deliverable.timeline}
                  onChangeText={(text) => updateDeliverable(deliverable.id, 'timeline', text)}
                />
              </View>
            ))}
          </View>
          
          <View style={styles.formSection}>
            <View style={styles.formSectionHeader}>
              <Text style={styles.formSectionTitle}>Proposal Sections</Text>
              <TouchableOpacity 
                style={styles.addButton}
                onPress={addSection}
              >
                <MaterialCommunityIcons name="plus" size={20} color="#3B82F6" />
                <Text style={styles.addButtonText}>Add Section</Text>
              </TouchableOpacity>
            </View>
            
            {proposalForm.sections.map((section, index) => (
              <View key={section.id} style={styles.sectionForm}>
                <View style={styles.sectionHeader}>
                  <Text style={styles.sectionTitle}>Section {index + 1}</Text>
                  <TouchableOpacity 
                    onPress={() => removeSection(section.id)}
                    style={styles.removeButton}
                  >
                    <MaterialCommunityIcons name="close" size={16} color="#EF4444" />
                  </TouchableOpacity>
                </View>
                <TextInput
                  style={styles.formInput}
                  placeholder="Section Title"
                  value={section.title}
                  onChangeText={(text) => updateSection(section.id, 'title', text)}
                />
                <TextInput
                  style={[styles.formInput, styles.textArea]}
                  placeholder="Section Content"
                  value={section.content}
                  onChangeText={(text) => updateSection(section.id, 'content', text)}
                  multiline
                  numberOfLines={4}
                />
              </View>
            ))}
          </View>
          
          <View style={styles.formSection}>
            <Text style={styles.formSectionTitle}>Terms & Conditions</Text>
            <TextInput
              style={[styles.formInput, styles.textArea]}
              placeholder="Payment Terms and Conditions"
              value={proposalForm.terms}
              onChangeText={(text) => setProposalForm({ ...proposalForm, terms: text })}
              multiline
              numberOfLines={3}
            />
          </View>
        </ScrollView>
        
        <View style={styles.modalFooter}>
          <TouchableOpacity 
            style={[styles.modalButton, styles.cancelButton]}
            onPress={() => setShowCreateModal(false)}
          >
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={[styles.modalButton, styles.saveButton]}
            onPress={handleSaveProposal}
          >
            <Text style={styles.saveButtonText}>Save Proposal</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  const renderProposalModal = () => (
    <Modal
      visible={showProposalModal}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => {
        setShowProposalModal(false);
        setSelectedProposal(null);
      }}
    >
      <View style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>
            {selectedProposal ? selectedProposal.id : 'Proposal Details'}
          </Text>
          <TouchableOpacity 
            style={styles.modalCloseButton}
            onPress={() => {
              setShowProposalModal(false);
              setSelectedProposal(null);
            }}
          >
            <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
          </TouchableOpacity>
        </View>
        
        {selectedProposal && (
          <ScrollView style={styles.modalContent}>
            <View style={styles.proposalPreview}>
              <View style={styles.proposalPreviewHeader}>
                <Text style={styles.proposalPreviewTitle}>{selectedProposal.title}</Text>
                <View style={[styles.statusBadge, { backgroundColor: getStatusColor(selectedProposal.status) }]}>
                  <MaterialCommunityIcons 
                    name={getStatusIcon(selectedProposal.status)} 
                    size={12} 
                    color="#FFFFFF" 
                  />
                  <Text style={styles.statusText}>{selectedProposal.status.replace('_', ' ')}</Text>
                </View>
              </View>
              
              <View style={styles.proposalPreviewSection}>
                <Text style={styles.proposalPreviewSectionTitle}>Client Information</Text>
                <Text style={styles.proposalPreviewText}>
                  {selectedProposal.clientName}{'\n'}
                  {selectedProposal.clientCompany}{'\n'}
                  {selectedProposal.clientEmail}
                </Text>
              </View>
              
              <View style={styles.proposalPreviewSection}>
                <Text style={styles.proposalPreviewSectionTitle}>Project Overview</Text>
                <Text style={styles.proposalPreviewText}>{selectedProposal.projectDescription}</Text>
              </View>
              
              {selectedProposal.objectives && selectedProposal.objectives.length > 0 && (
                <View style={styles.proposalPreviewSection}>
                  <Text style={styles.proposalPreviewSectionTitle}>Project Objectives</Text>
                  {selectedProposal.objectives.map((objective, index) => (
                    <Text key={index} style={styles.proposalPreviewText}>• {objective}</Text>
                  ))}
                </View>
              )}
              
              {selectedProposal.deliverables && selectedProposal.deliverables.length > 0 && (
                <View style={styles.proposalPreviewSection}>
                  <Text style={styles.proposalPreviewSectionTitle}>Deliverables</Text>
                  {selectedProposal.deliverables.map((deliverable, index) => (
                    <View key={index} style={styles.deliverablePreview}>
                      <Text style={styles.deliverablePreviewTitle}>{deliverable.title}</Text>
                      <Text style={styles.deliverablePreviewDescription}>{deliverable.description}</Text>
                      <Text style={styles.deliverablePreviewTimeline}>Timeline: {deliverable.timeline}</Text>
                    </View>
                  ))}
                </View>
              )}
              
              {selectedProposal.sections && selectedProposal.sections.length > 0 && (
                <View style={styles.proposalPreviewSection}>
                  <Text style={styles.proposalPreviewSectionTitle}>Proposal Sections</Text>
                  {selectedProposal.sections.map((section, index) => (
                    <View key={index} style={styles.sectionPreview}>
                      <Text style={styles.sectionPreviewTitle}>{section.title}</Text>
                      <Text style={styles.sectionPreviewContent}>{section.content}</Text>
                    </View>
                  ))}
                </View>
              )}
              
              <View style={styles.proposalPreviewSection}>
                <Text style={styles.proposalPreviewSectionTitle}>Project Details</Text>
                <Text style={styles.proposalPreviewText}>
                  Budget: {formatCurrency(selectedProposal.budget)}{'\n'}
                  Timeline: {selectedProposal.timeline}{'\n'}
                  Valid Until: {formatDate(selectedProposal.validUntil)}
                </Text>
              </View>
              
              {selectedProposal.terms && (
                <View style={styles.proposalPreviewSection}>
                  <Text style={styles.proposalPreviewSectionTitle}>Terms & Conditions</Text>
                  <Text style={styles.proposalPreviewText}>{selectedProposal.terms}</Text>
                </View>
              )}
            </View>
          </ScrollView>
        )}
        
        <View style={styles.modalFooter}>
          <TouchableOpacity 
            style={[styles.modalButton, styles.exportButton]}
            onPress={() => Alert.alert('Export', 'Proposal would be exported as PDF')}
          >
            <MaterialCommunityIcons name="download" size={16} color="#FFFFFF" />
            <Text style={styles.exportButtonText}>Export PDF</Text>
          </TouchableOpacity>
          {selectedProposal?.status === 'draft' && (
            <TouchableOpacity 
              style={[styles.modalButton, styles.sendButton]}
              onPress={() => {
                handleSendProposal(selectedProposal.id);
                setShowProposalModal(false);
                setSelectedProposal(null);
              }}
            >
              <MaterialCommunityIcons name="send" size={16} color="#FFFFFF" />
              <Text style={styles.sendButtonText}>Send Proposal</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    </Modal>
  );

  if (isLoading) {
    return (
      <SafeAreaView style={styles.loadingContainer}>
        <ActivityIndicator size={36} color="#3B82F6" />
        <Text style={styles.loadingText}>Loading Proposal Builder...</Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#1F2937" />
      
      {renderHeader()}
      {renderTabs()}
      {renderContent()}
      {renderCreateModal()}
      {renderProposalModal()}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
  },
  
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#6B7280',
    fontWeight: '500',
  },
  
  // Header styles
  header: {
    paddingTop: 12,
    paddingBottom: 16,
    paddingHorizontal: 16,
  },
  
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  headerCenter: {
    flex: 1,
    alignItems: 'center',
  },
  
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 2,
  },
  
  headerButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  // Tabs styles
  tabsContainer: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  
  tabsContent: {
    paddingHorizontal: 16,
  },
  
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginRight: 8,
    borderRadius: 20,
  },
  
  activeTab: {
    backgroundColor: '#EBF4FF',
  },
  
  tabText: {
    fontSize: 14,
    color: '#6B7280',
    marginLeft: 8,
    fontWeight: '500',
  },
  
  activeTabText: {
    color: '#3B82F6',
    fontWeight: '600',
  },
  
  // Content styles
  contentContainer: {
    flex: 1,
  },
  
  listContainer: {
    padding: 16,
  },
  
  // Search styles
  searchContainer: {
    padding: 16,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F3F4F6',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#1F2937',
    marginLeft: 8,
  },
  
  // Stats styles
  statsContainer: {
    padding: 16,
  },
  
  row: {
    justifyContent: 'space-between',
  },
  
  statCard: {
    width: (width - 48) / 2,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    alignItems: 'center',
  },
  
  statIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  
  statValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 4,
  },
  
  statTitle: {
    fontSize: 12,
    color: '#6B7280',
    textAlign: 'center',
  },
  
  // Proposal card styles
  proposalCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  
  proposalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  
  proposalInfo: {
    flex: 1,
  },
  
  proposalId: {
    fontSize: 14,
    fontWeight: '600',
    color: '#3B82F6',
    marginBottom: 4,
  },
  
  proposalTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4,
  },
  
  proposalClient: {
    fontSize: 14,
    color: '#6B7280',
  },
  
  proposalStatus: {
    alignItems: 'flex-end',
  },
  
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  
  statusText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 4,
    textTransform: 'capitalize',
  },
  
  proposalDescription: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
    marginBottom: 12,
  },
  
  proposalDetails: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  
  proposalDetail: {
    flex: 1,
  },
  
  proposalDetailLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 4,
  },
  
  proposalDetailValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  proposalActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    marginLeft: 8,
  },
  
  actionButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 4,
  },
  
  // Template card styles
  templateCard: {
    width: (width - 48) / 2,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  
  templateHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  
  templateName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    flex: 1,
  },
  
  templateCategory: {
    backgroundColor: '#EBF4FF',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  
  templateCategoryText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#3B82F6',
  },
  
  templateDescription: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 12,
  },
  
  templateDetails: {
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    paddingTop: 8,
  },
  
  templateDetail: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 2,
  },
  
  // Analytics styles
  analyticsSection: {
    backgroundColor: '#FFFFFF',
    margin: 16,
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 16,
  },
  
  statusDistribution: {
    flexDirection: 'column',
  },
  
  statusItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  
  statusIndicator: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 12,
  },
  
  statusLabel: {
    flex: 1,
    fontSize: 14,
    color: '#6B7280',
    textTransform: 'capitalize',
  },
  
  statusCount: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  // Modal styles
  modalContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
    flex: 1,
  },
  
  modalCloseButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#F3F4F6',
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  modalContent: {
    flex: 1,
    padding: 16,
  },
  
  modalFooter: {
    flexDirection: 'row',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
  
  modalButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 8,
    marginHorizontal: 4,
  },
  
  cancelButton: {
    backgroundColor: '#F3F4F6',
  },
  
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#6B7280',
  },
  
  saveButton: {
    backgroundColor: '#3B82F6',
  },
  
  saveButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  
  exportButton: {
    backgroundColor: '#6B7280',
  },
  
  exportButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 8,
  },
  
  sendButton: {
    backgroundColor: '#3B82F6',
  },
  
  sendButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 8,
  },
  
  // Form styles
  formSection: {
    marginBottom: 24,
  },
  
  formSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 12,
  },
  
  formSectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  
  formInput: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: '#1F2937',
    backgroundColor: '#FFFFFF',
    marginBottom: 12,
  },
  
  textArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#3B82F6',
  },
  
  addButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#3B82F6',
    marginLeft: 4,
  },
  
  deliverableForm: {
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
  },
  
  deliverableHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  
  deliverableTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  removeButton: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#FEE2E2',
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  sectionForm: {
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
  },
  
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  
  sectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  // Proposal preview styles
  proposalPreview: {
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    padding: 16,
  },
  
  proposalPreviewHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  
  proposalPreviewTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
    flex: 1,
  },
  
  proposalPreviewSection: {
    marginBottom: 16,
  },
  
  proposalPreviewSectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 8,
  },
  
  proposalPreviewText: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
    marginBottom: 4,
  },
  
  deliverablePreview: {
    backgroundColor: '#FFFFFF',
    borderRadius: 6,
    padding: 12,
    marginBottom: 8,
  },
  
  deliverablePreviewTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4,
  },
  
  deliverablePreviewDescription: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 4,
  },
  
  deliverablePreviewTimeline: {
    fontSize: 12,
    color: '#3B82F6',
    fontWeight: '500',
  },
  
  sectionPreview: {
    backgroundColor: '#FFFFFF',
    borderRadius: 6,
    padding: 12,
    marginBottom: 8,
  },
  
  sectionPreviewTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 8,
  },
  
  sectionPreviewContent: {
    fontSize: 12,
    color: '#6B7280',
    lineHeight: 18,
  },
});
